/**
 * captureLinks — everything the "Link" capture type decides, as pure functions
 * (QA-98 + QA-99).
 *
 * QA-98 (Gordon, verbatim): "Capture needs an additional media type for
 * Links/URLs called 'Link' — users can copy/paste links there. Support Embed
 * URLs for Videos and Images, to match what the Wheel Builder blocks can
 * already do."
 *
 * QA-99 (Gordon, verbatim): "If Capture media is sourced from an Embed URL or a
 * Shared URL from a 3rd party (e.g. YouTube), we need to SAVE the source URL on
 * the capture item."
 *
 * No React, no network. Each decision is asserted on its own, so a failure names
 * the decision that broke rather than sending a reader into a render log.
 *
 * THE SECURITY RULE, first because everything else depends on it: a capture's
 * URL is rendered as an `href` and, for a provider URL, resolved into an embed.
 * A `javascript:` or `data:` URL in an href is a STORED XSS sink — one user
 * pastes it, and it runs in the browser of everyone who later views that
 * capture. So the scheme is an ALLOWLIST of exactly http and https. Everything
 * else is refused by default rather than enumerated and denied, because a
 * denylist cannot cover a scheme nobody has thought of yet. The backend
 * enforces the identical rule (capture/api/serializers.py) — this copy is the
 * user-facing half that explains the refusal before a round trip, never the
 * security boundary on its own.
 */

/** Mirrors the backend's MAX_SOURCE_URL_LENGTH. */
export const MAX_LINK_LENGTH = 2048;

const ALLOWED_SCHEMES = ["http://", "https://"];

/**
 * Trim the punctuation a user drags in around a URL WITHOUT touching the URL
 * itself (QA-121 / capture link-paste). Pasting a link out of a sentence brings
 * its wrapper with it — "(https://ex.com/a)", "https://ex.com/a." — and that
 * trailing "." or ")" was being SAVED as part of the URL, so the stored link
 * was broken (404 / wrong anchor). Only the boundary is touched: characters in
 * the MIDDLE of the URL are never a wrapper and are left exactly alone.
 *
 * A trailing bracket is kept when it BALANCES one inside the URL — a real
 * Wikipedia link like `.../Foo_(bar)` owns its `)`, whereas a `)` with no
 * matching `(` is the sentence's, not the URL's. This is the standard autolink
 * boundary rule; it changes only what was never part of the link.
 */
const LEADING_WRAP = /^[\s('"<[{‘“]+/;
const TRAILING_WRAP = new Set([
  ".", ",", ";", ":", "!", "?", "'", '"',
  ")", "]", "}", ">", "’", "”",
]);

const countChar = (str, ch) => {
  let n = 0;
  for (let i = 0; i < str.length; i += 1) if (str[i] === ch) n += 1;
  return n;
};

export const trimLinkBoundary = (raw) => {
  let v = String(raw ?? "").trim().replace(LEADING_WRAP, "");
  while (v.length) {
    const last = v[v.length - 1];
    if (!TRAILING_WRAP.has(last)) break;
    // A closing bracket that pairs with an opener inside the URL belongs to it.
    if (last === ")" && countChar(v, "(") >= countChar(v, ")")) break;
    if (last === "]" && countChar(v, "[") >= countChar(v, "]")) break;
    if (last === "}" && countChar(v, "{") >= countChar(v, "}")) break;
    v = v.slice(0, -1);
  }
  return v;
};

/**
 * Tidy what the user pasted WITHOUT changing what it points at.
 *
 * A bare "youtube.com/watch?v=x" is what a user copies out of an address bar
 * more often than not, and refusing it for want of a scheme is a papercut. It
 * is promoted to https — never to http, which would silently downgrade the
 * request. Anything that already carries a scheme is left exactly as typed
 * (after boundary punctuation is trimmed), so a `javascript:` paste is NOT
 * rewritten into something that looks safe; it is carried through unchanged and
 * then refused below.
 */
export const normalizeCaptureLink = (raw) => {
  const value = trimLinkBoundary(raw);
  if (!value) return "";
  if (value.includes(":")) return value;
  return `https://${value}`;
};

/**
 * Why this link cannot be captured, in the user's words. Null when it is fine.
 * The message names the specific rule that failed — "Enter a valid URL" for
 * every failure tells a user nothing about which of several rules they broke.
 */
export const captureLinkError = (raw) => {
  const url = normalizeCaptureLink(raw);
  if (!url) return "Paste a link to capture.";
  if (url.length > MAX_LINK_LENGTH) {
    return `That link is too long (max ${MAX_LINK_LENGTH} characters).`;
  }
  // Control characters, including the tab/newline used to smuggle a scheme past
  // a naive prefix check ("java\tscript:alert(1)").
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(url)) {
    return "That link contains invalid characters.";
  }
  if (!ALLOWED_SCHEMES.some((s) => url.toLowerCase().startsWith(s))) {
    return "Links must start with http:// or https://.";
  }
  const remainder = url.split("//", 2)[1] || "";
  if (!remainder.trim()) return "Paste a link to capture.";
  // QA-1070 — a bare word ("ZZJUNK-not-a-url") is promoted to
  // "https://<word>", which clears the scheme allowlist above yet is not a
  // reachable address: saved, it was coerced to "https://zzjunk-not-a-url/"
  // and fired ERR_NAME_NOT_RESOLVED. A real host is a dotted name
  // (example.com, 127.0.0.1) or "localhost"; anything else is a typo, not a
  // URL, and must be refused before it becomes a broken Link capture.
  let host;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch (err) {
    return "Enter a valid web address, e.g. https://example.com.";
  }
  const hasDottedLabel = /^[^.]+\.[^.]+/.test(host);
  if (host !== "localhost" && !hasDottedLabel) {
    return "Enter a valid web address, e.g. https://example.com.";
  }
  return null;
};

/** True when the pasted value is a link this app will store. */
export const isValidCaptureLink = (raw) => captureLinkError(raw) === null;

/**
 * QA-121 — "This link is missing or can't be opened" false positive on a
 * real, live, reachable URL.
 *
 * ROOT CAUSE: a real-world link paste is rarely a bare URL. Sharing a page
 * from a message, a social post, or a browser's own "Copy Link" hands the
 * clipboard "Title https://example.com/page" — title first, URL after —
 * because that is how link-sharing actually works everywhere else. Pasted
 * whole into the single "Paste a link" field, `captureLinkError` correctly
 * refuses it (the string does not START with an allowed scheme), and the
 * item never reaches the point where THIS extraction can help unless the
 * caller uses it. Extracting the real URL and treating the rest as the
 * title is exactly what QA-98 promised ("users can copy/paste links there")
 * — a user should not have to hand-edit their clipboard first.
 *
 * Only ever extracts an http(s) substring: a scheme this function does not
 * recognise (`javascript:`, `data:`, …) is never pulled out and handed back
 * as a "url", so a disguised unsafe scheme still falls through to
 * `captureLinkError`'s refusal exactly as it does today — this is a paste
 * convenience, never a second, more permissive validator.
 *
 * Returns `null` when there is nothing to split (no embedded URL at all, or
 * the input is ALREADY a bare URL with nothing around it), so a caller can
 * fall back to validating the raw paste unchanged.
 */
const EMBEDDED_URL_RE = /https?:\/\/[^\s<>"']+/i;

export const splitPastedLink = (raw) => {
  const value = raw || "";
  const match = value.match(EMBEDDED_URL_RE);
  if (!match) return null;

  const url = match[0];
  if (value.trim() === url) return null; // already bare — nothing to split

  const title = (
    value.slice(0, match.index) + value.slice(match.index + url.length)
  )
    // Strip dangling punctuation a title/url separator leaves behind
    // ("Read later - https://…" -> "Read later -" -> "Read later").
    .replace(/[\s\-–—|:]+$/, "")
    .replace(/^[\s\-–—|:]+/, "")
    .trim();

  return { url, title };
};

/** The host, for display on the card. "" when the link is unparseable. */
export const linkHost = (url) => {
  try {
    const parsed = new URL(normalizeCaptureLink(url));
    return parsed.hostname.replace(/^www\./, "");
  } catch (err) {
    return "";
  }
};

/**
 * Host plus pathname for link labels on cards (e.g. web-stg.iyuga.net/discover).
 * Query and hash are omitted; when there is no path beyond "/", returns host only.
 */
export const linkDisplayLabel = (url) => {
  try {
    const parsed = new URL(normalizeCaptureLink(url));
    const host = parsed.hostname.replace(/^www\./, "");
    let path = parsed.pathname || "";
    if (path.length > 1 && path.endsWith("/")) {
      path = path.slice(0, -1);
    }
    if (!path || path === "/") return host;
    return `${host}${path}`;
  } catch (err) {
    return "";
  }
};

/**
 * A favicon for the link's host, served by the host itself.
 *
 * Deliberately the site's own `/favicon.ico` rather than a third-party favicon
 * proxy: a proxy would tell an outside service every URL every iYuga user has
 * ever captured, which is a privacy leak nobody asked for in exchange for a
 * 16px picture. A missing favicon is handled by the caller falling back to the
 * line-art link glyph.
 */
export const linkFaviconUrl = (url) => {
  try {
    const parsed = new URL(normalizeCaptureLink(url));
    if (!ALLOWED_SCHEMES.some((s) => `${parsed.protocol}//`.startsWith(s))) return null;
    return `${parsed.protocol}//${parsed.hostname}/favicon.ico`;
  } catch (err) {
    return null;
  }
};

/* ---------------------------------------------------------------------- */
/* Provider detection — "the same embed treatment the Wheel Builder blocks   */
/* already support" (QA-98).                                                 */
/* ---------------------------------------------------------------------- */

export const LINK_KIND = {
  /** A video a provider will embed (YouTube, Vimeo, Loom, Dailymotion). */
  VIDEO: "video",
  /** A direct image URL. */
  IMAGE: "image",
  /** Anything else: a page. It becomes a link, not an embed. */
  PAGE: "page",
};

const VIDEO_HOSTS = [
  "youtube.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
  "loom.com",
  "dailymotion.com",
  "dai.ly",
];

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".avif", ".svg"];

/**
 * What kind of thing is at the end of this link?
 *
 * Host matching is on the registrable host and its subdomains
 * (`m.youtube.com`, `player.vimeo.com`), NOT a substring test: a substring test
 * would classify `https://youtube.com.evil.example/` as YouTube, which is the
 * classic look-alike-domain mistake and would hand an attacker an embed.
 */
export const linkKind = (url) => {
  let parsed;
  try {
    parsed = new URL(normalizeCaptureLink(url));
  } catch (err) {
    return LINK_KIND.PAGE;
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  if (VIDEO_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) {
    return LINK_KIND.VIDEO;
  }
  const path = parsed.pathname.toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext))) {
    return LINK_KIND.IMAGE;
  }
  return LINK_KIND.PAGE;
};

/**
 * The source URL to show on a capture, whatever its type (QA-99).
 *
 * A LINK capture's own URL and an uploaded capture's provenance are the same
 * field on the backend, so one reader serves both and a caller never has to
 * branch on type to answer "where did this come from?".
 */
export const captureSourceUrl = (item) => {
  if (!item) return null;
  const url = (item.source_url || "").trim();
  return url && isValidCaptureLink(url) ? url : null;
};

/**
 * Should this capture display its provenance line?
 *
 * Only when there is something the user did not already see: a LINK capture
 * already shows its URL as the payload, so repeating it underneath as "from …"
 * is noise. An uploaded photo or video with a recorded source is the case
 * QA-99 exists for.
 */
export const showsProvenance = (item) =>
  Boolean(item && item.type !== "link" && captureSourceUrl(item));
